<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h2 class="mb-4">
          Employee Attendance Records
        </h2>

        <!-- Summary Cards -->
        <v-row class="mb-4">
          <v-col
            cols="12"
            sm="4">
            <v-card
              color="success"
              dark
              elevation="2">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item class="pl-0">
                    Total Employees Checkin
                  </v-list-item>
                  <v-list-item-title class="headline">
                    {{ total }} Persons
                  </v-list-item-title>
                </v-list-item-content>
                <v-icon large>
                  mdi-account-group
                </v-icon>
              </v-list-item>
            </v-card>
          </v-col>
        </v-row>

        <!-- Filters & Actions -->
        <v-card
          class="pa-4 mb-4"
          outlined>
          <v-row align="center">
            <v-col
              cols="12"
              sm="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRangeText"
                    label="Filter by Date Range"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dates"
                  range
                  @input="onDateChange"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-spacer></v-spacer>
            <v-col
              cols="12"
              sm="auto">
              <v-btn
                color="grey darken-3"
                dark
                @click="exportXLSX">
                <v-icon left>
                  mdi-file-export
                </v-icon>
                Export XLSX
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- Attendance Table -->
        <v-card outlined>
          <v-data-table
            :headers="headers"
            :items="items"
            :options.sync="options"
            :items-per-page="options.itemsPerPage"
            :server-items-length="total"
            :loading="loading"
            :footer-props="{'items-per-page-options': [5, 15, 25, 50]}"
            class="w-full"
            @update:options="fetchAttendance()"
          >
            <!-- Custom Formatting for Employee Column -->
            <template v-slot:item.employee="{ item }">
              <div class="py-2">
                <strong>{{ item.employee.firstName }} {{ item.employee.lastName }}</strong><br>
                <small class="text--secondary">{{ item.employee.employeeCode }} | {{ item.employee.department }}</small>
              </div>
            </template>

            <!-- Format Time -->
            <template v-slot:item.checkInAt="{ item }">
              {{ formatTime(item.checkInAt) }}
            </template>
            <template v-slot:item.checkoutAt="{ item }">
              {{ formatTime(item.checkoutAt) }}
            </template>

            <!-- Duration in Hours -->
            <template v-slot:item.duration="{ item }">
              {{ formatDuration(item.duration) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import WarehouseWorkTrackingProvider from '@/resources/WarehouseWorkTrackingProvider'
import * as XLSX from 'xlsx'

const provider = new WarehouseWorkTrackingProvider()

export default {
  data: () => ({
    loading: false,
    dateMenu: false,
    dates: [],
    options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: ['createdAt'],
      sortDesc: [true]
    },
    total: 0,
    headers: [
      { text: 'Employee', value: 'employee', sortable: false },
      { text: 'Date', value: 'date' },
      { text: 'Check-in', value: 'checkInAt' },
      { text: 'Check-out', value: 'checkoutAt' },
      { text: 'Duration', value: 'duration', align: 'center' }
    ],
    items: []
  }),
  computed: {
    dateRangeText () {
      return this.dates.join(' ~ ')
    }
  },
  mounted () {
    // ตั้ง default dates เป็น 7 วันล่าสุด
    const today = new Date().toISOString().split('T')[0]
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0]
    this.dates = [weekAgo, today]
    this.fetchAttendance()
  },
  methods: {
    formatDuration (ms) {
      if (!ms) return '-'
      const seconds = Math.floor((ms / 1000) % 60)
      const minutes = Math.floor((ms / (1000 * 60)) % 60)
      const hours = Math.floor(ms / (1000 * 60 * 60))
      const parts = []
      if (hours > 0) parts.push(`${hours}h`)
      if (minutes > 0 || hours > 0) parts.push(`${minutes}m`)
      parts.push(`${seconds}s`)
      return parts.join(' ')
    },
    async fetchAttendance () {
      if (this.dates.length !== 2) return
      this.loading = true
      try {
        const sorted = [...this.dates].sort()
        const { data: { results, total } } = await provider.getAttendanceRange({
          startDate: sorted[0],
          endDate: sorted[1],
          page: this.options.page,
          limit: this.options.itemsPerPage,
          sortBy: this.options.sortBy[0],
          sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc'
        })
        const rawData = results || []
        this.total = total
        this.items = rawData.map((item) => ({
          ...item,
          employee: item.employeeId || {}
        }))
      } catch (err) {
        console.error('Failed to fetch attendance:', err)
        this.$store.dispatch('Style/setSnackbarStyle', {
          active: true,
          text: 'ไม่สามารถโหลดข้อมูลการเข้างานได้',
          color: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    formatTime (dateTimeStr) {
      if (!dateTimeStr) return '-'
      const date = new Date(dateTimeStr)
      return date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
    },
    onDateChange () {
      if (this.dates.length === 2) {
        this.fetchAttendance()
      }
    },
    async exportXLSX () {
      if (this.dates.length !== 2) return
      this.loading = true
      try {
        const sorted = [...this.dates].sort()
        const { data: { results } } = await provider.getAttendanceRange({
          startDate: sorted[0],
          endDate: sorted[1],
          page: 1,
          limit: 99999,
          sortBy: this.options.sortBy[0],
          sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc'
        })

        const rows = (results || []).map((row) => {
          const emp = row.employeeId || {}
          return {
            'Employee': `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
            'Code': emp.employeeCode || '',
            'Department': emp.department || '',
            'Date': row.date || '',
            'Check-in': this.formatTime(row.checkInAt),
            'Check-out': this.formatTime(row.checkoutAt),
            'Duration (hrs)': row.duration ? (row.duration / 3600000).toFixed(2) : '0'
          }
        })

        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Attendance')
        XLSX.writeFile(wb, `attendance_report_${sorted[0]}_${sorted[1]}.xlsx`)
      } catch (err) {
        console.error('Failed to export attendance:', err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 8px;
}
</style>
